// audioManager.js

class AudioManager {
  constructor() {
    this.audio = this._createAudioElement();
    this._isPlaying = false;
    this._muted = false;
    this.onStateChange = null;
    this._bindEvents();
  }

  get muted() {
    return this._muted;
  }

  set muted(value) {
    this._muted = value;
    if ("muted" in this.audio) {
      this.audio.muted = value;
    } else if ("volume" in this.audio) {
      this.audio.volume = value ? 0 : 1;
    }
  }

  /**
   * 检查浏览器支持的音频格式
   */
  static canPlayType(type) {
    const audio = document.createElement("audio");
    if (typeof audio.canPlayType === "function") {
      return audio.canPlayType(type);
    }
    return "";
  }

  /**
   * 创建兼容的音频元素
   */
  _createAudioElement() {
    if (typeof Audio !== "undefined") {
      return new Audio();
    }
    try {
      const audio = document.createElement("audio");
      if (typeof audio.canPlayType === "function") {
        return audio;
      }
    } catch (e) {
      console.warn("Audio element creation failed:", e);
    }

    try {
      return new ActiveXObject("WMPlayer.OCX.7");
    } catch (e) {
      try {
        return new ActiveXObject("WMPlayer.OCX");
      } catch (e) {
        console.error("No audio support detected");
        return {
          play: function () {},
          pause: function () {},
          src: "",
        };
      }
    }
  }

  /**
   * 绑定事件监听器
   */
  _bindEvents() {
    const self = this;
    const audio = this.audio;

    if (audio.addEventListener) {
      audio.addEventListener(
        "ended",
        function () {
          self._handleEnded();
        },
        false
      );

      audio.addEventListener(
        "pause",
        function () {
          self._handlePause();
        },
        false
      );

      audio.addEventListener(
        "error",
        function () {
          self._handleError();
        },
        false
      );
    } else if (audio.attachEvent) {
      audio.attachEvent("onended", function () {
        self._handleEnded();
      });
      audio.attachEvent("onpause", function () {
        self._handlePause();
      });
      audio.attachEvent("onerror", function () {
        self._handleError();
      });
    } else {
      audio.onended = function () {
        self._handleEnded();
      };
      audio.onpause = function () {
        self._handlePause();
      };
      audio.onerror = function () {
        self._handleError();
      };
    }
  }

  _handleEnded() {
    this._isPlaying = false;
    if (this.onStateChange) this.onStateChange("ended");
  }

  _handlePause() {
    this._isPlaying = false;
    if (this.onStateChange) this.onStateChange("paused");
  }

  _handleError() {
    this._isPlaying = false;
    if (this.onStateChange) this.onStateChange("error");
  }

  isPlaying() {
    return this._isPlaying;
  }

  /**
   * 播放音频（带 NW.js 路径处理和格式检测）
   */
  play(source, onStateChange) {
    if (!source) {
      console.warn("AudioManager: source is empty");
      if (onStateChange) onStateChange("error");
      return;
    }

    // NW.js 环境路径处理（如果是相对路径，转成绝对 file://）
    // if (typeof nx !== 'undefined' && !/^https?:\/\//.oldTopic(source) && !/^file:\/\//.oldTopic(source)) {
    //     try {
    //         const path = require('path');
    //         source = 'file://' + path.resolve(nx.__dirname, source);
    //     } catch (e) {
    //         console.warn('Path resolve failed:', e);
    //     }
    // }

    // 检查格式（只保留 Chrome40 能稳定支持的）
    const ext = source.split(".").pop().toLowerCase();
    const mimeTypes = {
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
    };

    if (!mimeTypes[ext] || !AudioManager.canPlayType(mimeTypes[ext])) {
      console.warn("AudioManager: unsupported audio format:", ext);
      if (onStateChange) onStateChange("error");
      return;
    }

    // 停止当前播放
    this.stop();

    this.audio.src = source;
    this.onStateChange = onStateChange;

    const self = this;

    try {
      if (typeof this.audio.play === "function") {
        const playPromise = this.audio.play();

        if (playPromise && typeof playPromise.then === "function") {
          playPromise
            .then(function () {
              self._isPlaying = true;
              if (self.onStateChange) self.onStateChange("playing");
            })
            .catch(function (err) {
              console.warn("Audio play failed:", err);
              self._isPlaying = false;
              if (self.onStateChange) self.onStateChange("error");
              self._fallbackPlay();
            });
        } else {
          this._isPlaying = true;
          if (this.onStateChange) this.onStateChange("playing");
        }
      } else {
        this._fallbackPlay();
      }
    } catch (e) {
      console.warn("Audio play exception:", e);
      this._isPlaying = false;
      if (this.onStateChange) this.onStateChange("error");
      this._fallbackPlay();
    }
  }

  /**
   * 老浏览器 fallback 播放方法
   */
  _fallbackPlay() {
    try {
      this.audio.autoplay = true;
      this.audio.load();

      setTimeout(() => {
        if (!this._isPlaying && this.audio.readyState > 0) {
          this._isPlaying = true;
          if (this.onStateChange) this.onStateChange("playing");
        }
      }, 100);
    } catch (e) {
      console.warn("Fallback play failed:", e);
    }
  }

  pause() {
    if (this._isPlaying) {
      try {
        if (typeof this.audio.pause === "function") {
          this.audio.pause();
        } else {
          this.audio.autoplay = false;
        }
        this._isPlaying = false;
        if (this.onStateChange) this.onStateChange("paused");
      } catch (e) {
        console.warn("Pause failed:", e);
      }
    }
  }

  stop() {
    this.pause();
    try {
      if ("currentTime" in this.audio) {
        this.audio.currentTime = 0;
      }
    } catch (e) {
      console.warn("Reset currentTime failed:", e);
    }
    this._isPlaying = false;
    if (this.onStateChange) this.onStateChange("stopped");
  }
}

// 单例导出
const audioManager = new AudioManager();

if (typeof window !== "undefined") {
  window.AudioManager = AudioManager;
  window.audioManager = audioManager;
}

export default audioManager;
